import { defineAsyncComponent } from 'vue';
import type { Composition } from '@spreadgroup/common-partner-lib/composition';
import dialogService from 'commons/dialogs/wrapper/dialogService';
import publishingCoreService from 'commons/api/publishingCoreService/publishingCoreService';
import {
  PUBLISHING_SCOPE,
  type PublishingScope,
} from 'commons/publishing/publishingScope';
import { availableSampleOrderChannels } from './sampleOrderChannels';
import { hasSampleableProducts } from './sampleOrderEligibility';
import { openSampleOrderJourney } from './sampleOrderJourney';
import type { SampleOrderSource } from './sampleOrderTracking';

/**
 * Compositions per page, matching the composition list page. This list comes from the *expensive*
 * endpoint (`getCompositionsWithSellables`), so this is the number to lower if that ever shows.
 */
export const SAMPLE_ORDER_PAGE_SIZE = 20;

export interface SampleOrderCompositionPage {
  list: Composition[];
  count: number;
}

const SampleOrderEntryDialog = defineAsyncComponent(
  () =>
    import('src/app/partnerarea/sampleOrder/entry/SampleOrderEntryDialog.vue'),
);

/**
 * The one composition to hand over without asking, if there is one.
 *
 * `count` is the total rather than the page length. A lone composition *without* products does not
 * qualify — the dialog is where that case gets explained.
 */
export const soleSampleableComposition = (
  page: SampleOrderCompositionPage,
): Composition | undefined =>
  page.count === 1 && hasSampleableProducts(page.list[0])
    ? page.list[0]
    : undefined;

/** `shopId` narrows the list to one shop, where publishing-core cannot search — hence the dropped query. */
export const loadSampleOrderCompositions = (
  scope: PublishingScope,
  {
    offset = 0,
    query = null,
    shopId = null,
  }: {
    offset?: number;
    query?: string | null;
    shopId?: number | string | null;
  } = {},
): Promise<SampleOrderCompositionPage> =>
  publishingCoreService.getCompositionsWithSellables({
    scope: scope.key,
    limit: SAMPLE_ORDER_PAGE_SIZE,
    offset,
    query: shopId ? null : query,
    shopId,
  });

/**
 * The way into a sample order from outside a composition page: the dashboard module, the nav item and
 * the Shop Settings link call this and nothing else. It supplies `openSampleOrderJourney`'s input —
 * a composition and the channel it is ordered for — by asking for a channel, then a composition,
 * skipping either when there is nothing to choose.
 *
 * `scope` starts on the composition list instead of the channel step. `shopId` does the same and
 * narrows that list to one shop. It implies the Shop scope rather than taking one, so that a
 * meaningless `{ scope: MP, shopId }` pair cannot be built.
 *
 * Rejects with `'dialogDismiss'` when the partner backs out, so a caller can ignore the promise.
 * See `docs/publishing/sample-order-journey.md` §2.
 */
export const openSampleOrderEntry = async ({
  scope,
  shopId,
  source,
}: {
  scope?: PublishingScope;
  shopId?: number | string;
  source: SampleOrderSource;
}): Promise<unknown> => {
  const channels = availableSampleOrderChannels();

  if (!channels.length) {
    // The triggers hide themselves in this case; reaching here means something changed underneath.
    return undefined;
  }

  const startScope = shopId ? PUBLISHING_SCOPE.SHOP : scope;

  const { composition, scope: chosenScope } = (await dialogService.openDialog(
    SampleOrderEntryDialog,
    {
      channels,
      scope: startScope ?? (channels.length === 1 ? channels[0] : undefined),
      shopId,
      source,
    },
    // Without `staticBackdrop`, `v-click-outside` reads a click on a node the dialog re-rendered
    // away mid-gesture as a click outside and discards the selection. Escape still closes it.
    { staticBackdrop: true },
  )) as { composition: Composition; scope: PublishingScope };

  return openSampleOrderJourney(composition, {
    scope: chosenScope.key,
    source,
  });
};
